import { supabase } from '../utils/supabase';

/**
 * Resolve the session_games id from a history row. Profile, wrap-up, and
 * session-performance rows nest this differently.
 */
export function sessionGameIdFromHistoryRow(game) {
  return game?.session_games?.id || game?.session_game_id || game?.id || null;
}

/**
 * Map SCORE_CORRECTED host-action rows to the latest correction per game.
 * Later created_at wins when the same game is corrected more than once.
 *
 * @param {Array<{ created_at?: string, session_id?: string, payload?: object }>} rows
 * @returns {Map<string, { correctedAt: string|null, correctionMode: 'live'|'post_session', sessionId: string|null }>}
 */
export function mapScoreCorrectionActions(rows = []) {
  const byGameId = new Map();

  for (const row of rows) {
    const sessionGameId = row?.payload?.session_game_id;
    if (!sessionGameId) continue;

    const correctedAt = row.created_at || null;
    const existing = byGameId.get(sessionGameId);
    if (existing && new Date(existing.correctedAt || 0) >= new Date(correctedAt || 0)) {
      continue;
    }

    byGameId.set(sessionGameId, {
      correctedAt,
      correctionMode: row?.payload?.correction_mode === 'post_session' ? 'post_session' : 'live',
      sessionId: row.session_id || null,
    });
  }

  return byGameId;
}

export function hasPostSessionScoreCorrection(metaByGameId) {
  if (!metaByGameId || typeof metaByGameId.values !== 'function') return false;
  for (const meta of metaByGameId.values()) {
    if (meta?.correctionMode === 'post_session') return true;
  }
  return false;
}

/**
 * Load correction metadata for games in the given sessions.
 *
 * @param {string[]} sessionIds
 * @returns {Promise<Map<string, { correctedAt: string|null, correctionMode: 'live'|'post_session', sessionId: string|null }>>}
 */
export async function fetchScoreCorrectionMetaBySessionIds(sessionIds) {
  const ids = [...new Set((sessionIds || []).filter(Boolean))];
  if (ids.length === 0) return new Map();

  const { data, error } = await supabase
    .from('session_host_actions')
    .select('session_id, created_at, payload')
    .eq('action_type', 'SCORE_CORRECTED')
    .in('session_id', ids);

  if (error) {
    console.error('[scoreCorrectionMeta] fetch failed:', error);
    return new Map();
  }

  return mapScoreCorrectionActions(data || []);
}
